import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import DotSeparatedStrings from "../../../common/components/functional/dotSeparatedStrings/DotSeparatedStrings";
import InitialsAvatar from "../../../common/components/functional/initialsAvatar/InitialsAvatar";
import SkeletonWrapper from "../../../common/components/functional/skeletonWrapper/SkeletonWrapper";
import useLanguageSkills from "../../../hooks/useLanguageSkills";
import i18n from "../../../i18n";
import { OrderFields } from "../orderTypes";
import PreviewSalary from "./PreviewSalary";
import PreviewSectionContainer from "./PreviewSectionContainer";
import PreviewShifts from "./PreviewShifts";
import { PreviewImage } from "./styles";
import { ShiftDetailsType } from "./types";

type Props = {
  values: OrderFields;
  shiftDetails: ShiftDetailsType[];
};

const PreviewFull = ({ values, shiftDetails }: Props) => {
  const { t } = useTranslation("order");
  const currentLang = i18n.language;

  const localized = useAppSelector((state) => state.localized);

  const { getLanguageSkillsNameByCode } = useLanguageSkills();

  const location = `, ${values.workLocation.address.city}`;
  const workTitle = localized.workTitle[values.orderTemplate.workTitleId];
  const workTitleAndLocation = `${workTitle}${location}`;

  const multipleShifts = shiftDetails && shiftDetails.length > 1;

  const getDescription = () => {
    if (currentLang === "en") {
      return (
        values.descriptionEn ||
        values.workLocation.info[values.orderTemplate.workTitleId]?.en
      );
    }

    return (
      values.description ||
      values.workLocation.info[values.orderTemplate.workTitleId]?.fi
    );
  };

  const translatedCertificates = useMemo(() => {
    return values.orderTemplate.certificates.map((certificate) => ({
      id: certificate.id,
      name: localized.certificate[certificate.id],
    }));
  }, [values.orderTemplate.certificates, localized.certificate]);

  const translatedTrainings = useMemo(() => {
    return values.orderTemplate.trainings.map((training) => ({
      id: training.id,
      name: localized.training[training.id],
    }));
  }, [values.orderTemplate.trainings, localized.training]);

  const sortedCertificatesAndTrainings = useMemo(() => {
    return [...translatedCertificates, ...translatedTrainings].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [translatedCertificates, translatedTrainings]);

  const sortedSkills = useMemo(() => {
    return values.orderTemplate.skills
      .map((skill) => ({
        id: skill.id,
        name: localized.skill[skill.id],
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [values.orderTemplate.skills, localized.skill]);

  const sortedLanguageSkills = useMemo(() => {
    return values.orderTemplate.languages
      .map((language) => ({
        id: language.languageCode,
        name: getLanguageSkillsNameByCode(language.languageCode),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [values.orderTemplate.languages, getLanguageSkillsNameByCode]);

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <PreviewImage />
        <InitialsAvatar
          sx={{
            outline: "5px solid #fff",
            position: "absolute",
            bottom: 0,
            left: "50%",
            transform: "translate(-50%, 50%)",
          }}
          name={values.workLocation.name}
          width="65px"
          fontSize="27px"
        />
      </Box>
      <Box sx={{ padding: "1rem" }}>
        <Box sx={{ textAlign: "center", marginTop: "40px" }}>
          <SkeletonWrapper
            value={values.workLocation.name}
            width="30%"
            textAlign="center"
            height="20px"
            sx={{ marginBottom: "8px" }}
          >
            <Typography
              variant="header"
              sx={{ fontSize: "20px", fontWeight: 600 }}
            >
              {values.workLocation.name}
            </Typography>
          </SkeletonWrapper>
          <SkeletonWrapper
            value={workTitleAndLocation}
            width="50%"
            textAlign="center"
            height="12px"
          >
            <Typography>{workTitleAndLocation}</Typography>
          </SkeletonWrapper>
        </Box>
      </Box>
      <Box
        sx={{
          height: "43px",
          backgroundColor: "#EDDCFF",
          display: "flex",
          alignItems: "center",
          paddingLeft: "22px",
        }}
      >
        <Typography
          sx={{
            textTransform: "uppercase",
            fontSize: "12px",
            fontWeight: 700,
          }}
        >
          {multipleShifts ? t("offeredShifts") : t("offeredShift")}
        </Typography>
      </Box>
      <Box sx={{ padding: "8px 22px" }}>
        <PreviewSectionContainer>
          <PreviewShifts
            shiftDetails={shiftDetails}
            multipleShifts={multipleShifts}
            address={values?.workLocation?.address || ""}
          />
        </PreviewSectionContainer>
        <PreviewSectionContainer translationKey="common" title={"description"}>
          <SkeletonWrapper
            value={
              values.description ||
              values.workLocation.info[values.orderTemplate.workTitleId]?.fi
            }
            skeletonVariant="rectangular"
            height="60px"
            rows={[{ width: "90%" }, { width: "95%" }, { width: "80%" }]}
          >
            <Typography>{getDescription()}</Typography>
          </SkeletonWrapper>
        </PreviewSectionContainer>
        <PreviewSectionContainer translationKey="common" title="salaryLong">
          <PreviewSalary values={values} />
        </PreviewSectionContainer>
        <PreviewSectionContainer
          translationKey="order"
          title="requirementsLong"
        >
          <DotSeparatedStrings list={sortedCertificatesAndTrainings} />
        </PreviewSectionContainer>
        <PreviewSectionContainer translationKey="order" title="requiredSkills">
          <DotSeparatedStrings list={sortedSkills} />
        </PreviewSectionContainer>
        <PreviewSectionContainer
          translationKey="common"
          title="languageSkills"
          noBorder
        >
          <DotSeparatedStrings list={sortedLanguageSkills} />
        </PreviewSectionContainer>
      </Box>
    </>
  );
};

export default PreviewFull;
